import type {
  ImportMode,
  ProjectDto,
  ProjectImportResultDto,
  ProjectSummaryDto,
} from '@project-os/contracts';
import type { CreateProjectInput, UpdateProjectInput } from '@project-os/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, apiUpload } from './client';

export const projectKeys = {
  all: ['projects'] as const,
  list: (params: { includeArchived: boolean }) => ['projects', 'list', params] as const,
  detail: (projectId: string) => ['projects', 'detail', projectId] as const,
};

export function useProjects(options: { includeArchived?: boolean } = {}) {
  const includeArchived = options.includeArchived ?? false;
  return useQuery({
    queryKey: projectKeys.list({ includeArchived }),
    queryFn: ({ signal }) =>
      apiRequest<ProjectSummaryDto[]>(
        `/projects${includeArchived ? '?includeArchived=true' : ''}`,
        { signal },
      ),
  });
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.detail(projectId ?? ''),
    queryFn: ({ signal }) => apiRequest<ProjectDto>(`/projects/${projectId}`, { signal }),
    enabled: Boolean(projectId),
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      apiRequest<ProjectDto>('/projects', { method: 'POST', body: input }),
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(project.id), project);
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useUpdateProject(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProjectInput) =>
      apiRequest<ProjectDto>(`/projects/${projectId}`, { method: 'PATCH', body: input }),
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(projectId), project);
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useArchiveProject(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>(`/projects/${projectId}/archive`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

/**
 * Импорт проекта из файла выгрузки.
 *
 * Ответ не кладётся в кэш: это отчёт о разовой операции, а не состояние.
 * Сбрасывается всё, что относится к проектам: в режимах обновления и слияния
 * меняется существующий проект, а не только список.
 */
export function useImportProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      file: File;
      workspaceId: string;
      mode: ImportMode;
      name?: string;
      targetProjectId?: string;
    }) =>
      apiUpload<ProjectImportResultDto>('/projects/import', input.file, {
        fields: {
          workspaceId: input.workspaceId,
          mode: input.mode,
          name: input.name,
          targetProjectId: input.targetProjectId,
        },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['projects', result.projectId] });
    },
  });
}

/**
 * Логотип проекта.
 *
 * Сбрасывается и карточка проекта, и список: логотип виден в обоих местах,
 * а также в сайдбаре и переключателе, которые берут проект оттуда же.
 */
export function useUploadProjectLogo(projectId: string) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    void queryClient.invalidateQueries({ queryKey: projectKeys.all });
  };

  return useMutation({
    mutationFn: (file: File) => apiUpload<{ logoUrl: string }>(`/projects/${projectId}/logo`, file),
    onSuccess: refresh,
  });
}

export function useClearProjectLogo(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>(`/projects/${projectId}/logo`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}
